import React from "react";
import { useTranslation } from "react-i18next";
import FormNew from "@/components/controls/Form/FormNew";
import AddActionButton from "@/components/ui/add-action-button";
import { useFieldFormSections } from "../utils/fieldFormSections";
import { useFieldConfigurationData } from "../hooks/useFieldConfigurationData";
import { FieldFormData, OptionsManagerData } from "../utils/fieldFormUtils";
import { APIFieldDisplayTypeTargets, APIFieldItem } from "@/pages/settings/types";
import { ADD_CUSTOM_FIELD_TRANSLATION_KEY } from "../const";
import { EntityType } from "@/services/hooks/entities/useEntityType";

interface FieldFormContentProps {
  formData: FieldFormData;
  optionsManagerData: OptionsManagerData;
  fieldData?: APIFieldItem | null;
  formSetValue: any;
  showHelpText: boolean;
  showInCreation: boolean;
  isRequired: boolean;
  onShowHelpTextChange: (show: boolean) => void;
  onShowInCreationChange: (show: boolean) => void;
  onRequiredChange: (required: boolean) => void;
  onOptionsManagerChange: (data: OptionsManagerData) => void;
  onHiddenToggle?: (id: number | string) => void;
  onFilledByDreamerChange?: (filledByDreamer: boolean) => void;
  onFormValid: (valid: boolean, data: any) => void;
  onSubmit: () => Promise<void>;
  formRef: React.RefObject<HTMLFormElement>;
  onSetValue: (setValue: any) => void;
  isEditMode?: boolean;
  isPresetFieldEdit?: boolean;
  isEditableSystemField?: boolean;
  isWorkflowField?: boolean;
  entityType?: EntityType;
  bodyClassName?: string;
  displayTypeTargets?: APIFieldDisplayTypeTargets | null;
  shouldNotResetOnSubmit?: boolean;
}

export const FieldFormContent: React.FC<FieldFormContentProps> = ({
  formData,
  optionsManagerData,
  fieldData,
  formSetValue,
  showHelpText,
  showInCreation,
  isRequired,
  entityType,
  onShowHelpTextChange,
  onShowInCreationChange,
  onRequiredChange,
  onOptionsManagerChange,
  onHiddenToggle,
  onFilledByDreamerChange,
  onFormValid,
  onSubmit,
  formRef,
  onSetValue,
  isEditMode = false,
  isPresetFieldEdit = false,
  isEditableSystemField = false,
  isWorkflowField = false,
  bodyClassName = "p-5 w-full overflow-y-auto max-h-[calc(100vh-200px)]",
  displayTypeTargets = null,
  shouldNotResetOnSubmit = false
}) => {
  const { t } = useTranslation();

  const fieldConfigurationData = useFieldConfigurationData({
    fieldType: formData.fieldType,
    formData,
    optionsManagerData,
    fieldData,
    formSetValue,
    onOptionsManagerChange,
    onHiddenToggle,
    isPresetFieldEdit,
    isEditableSystemField
  });

  const formSections = useFieldFormSections({
    formData,
    optionsManagerData,
    fieldData,
    formSetValue,
    showHelpText,
    showInCreation,
    isRequired,
    entityType,
    onShowHelpTextChange,
    onShowInCreationChange,
    onRequiredChange,
    onOptionsManagerChange,
    onFilledByDreamerChange,
    isEditMode,
    isPresetFieldEdit,
    isEditableSystemField,
    isWorkflowField,
    fieldConfigurationData,
    displayTypeTargets
  });

  const processedSections = formSections.map((section) => {
    if (
      section.id === "field-label" &&
      section.bottomCustomComponent === "addHelpTextButton"
    ) {
      return {
        ...section,
        bottomCustomComponent: (
          <div className="flex justify-start mt-[-22px]">
            <AddActionButton
              label={t(`${ADD_CUSTOM_FIELD_TRANSLATION_KEY}.addHelpText`)}
              onClick={() => onShowHelpTextChange(true)}
              textVariant="body"
            />
          </div>
        )
      };
    }
    return section;
  });

  return (
    <div className={bodyClassName}>
      <FormNew
        sections={processedSections}
        isFormValid={onFormValid}
        onSubmit={onSubmit}
        formRef={formRef}
        onSetValue={onSetValue}
        shouldNotResetOnSubmit={shouldNotResetOnSubmit}
      />
    </div>
  );
};
